import { Button, Checkbox, Input, List, Modal, Space } from "antd"
import { useEffect, useState } from "react"
import { useImportVendorsMutation, useQboVendorsListQuery } from "./vendorsQueries"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowLeft, faCheck, faLink } from "@fortawesome/pro-solid-svg-icons"
import { blue } from "@ant-design/colors"

type Props = {
  open: boolean
  onClose: () => void
}

const ImportQuickBooksVendorsModal = ({
  open,
  onClose
}: Props) => {
  const importVendorsMutation = useImportVendorsMutation()
  const { data: qboVendorsListData, refetch } = useQboVendorsListQuery(true)
  const [searchFilter, setSearchFilter] = useState('')
  const [selectedVendors, setSelectedVendors] = useState<string[]>([])
  const filteredQboVendors = (qboVendorsListData ?? []).filter((vendor) => vendor.displayName.toLowerCase().includes(searchFilter.toLowerCase()))

  const handleSelect = (id: string) => {
    setSelectedVendors((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((vendorId) => vendorId !== id)
        : [...prevSelected, id]
    )
  }

  const handleSelectAll = (allVendors: string[]) => {
    if (selectedVendors.length === allVendors.length) {
      setSelectedVendors([])
    } else {
      setSelectedVendors(allVendors)
    }
  }

  const importVendors = () => {
    const payload = filteredQboVendors.filter((vendor) => selectedVendors.includes(vendor.id.toString()))
    importVendorsMutation.mutate(payload)
    onClose()
  }

  useEffect(() => {
    if (open) {
      refetch()
    }
  }, [open]);

  return (
    <Modal
      title={
        <Space>
          <FontAwesomeIcon icon={faLink} /> Import Vendors from QuickBooks
        </Space>
      }
      open={open}
      onCancel={onClose}
      footer={
        <>
          <Button key="back" onClick={onClose} icon={<FontAwesomeIcon icon={faArrowLeft} />}>
            Close
          </Button>
          <Button
            key="submit"
            type="primary"
            onClick={importVendors}
            disabled={selectedVendors.length === 0}
            icon={<FontAwesomeIcon icon={faCheck} />}
          >
            Import Selected ({selectedVendors.length})
          </Button>
        </>
      }
    >
      <Input
        value={searchFilter}
        style={{ marginBottom: 16 }}
        placeholder="Search QuickBooks vendors..."
        onChange={(e) => setSearchFilter(e.target.value)}
      />

      {(filteredQboVendors.length > 0) && (
        <Checkbox
          onChange={() => handleSelectAll(filteredQboVendors.map((vendor) => vendor.id.toString()))}
          checked={selectedVendors.length === filteredQboVendors.length}
          style={{ marginBottom: 16 }}
        >
          Select all vendors
        </Checkbox>
      )}

      <VendorList
        vendors={filteredQboVendors}
        selectedVendors={selectedVendors}
        onSelect={handleSelect}
      />
    </Modal>
  )
}

export default ImportQuickBooksVendorsModal

const VendorList = ({
  vendors,
  selectedVendors,
  onSelect,
}: {
  vendors: { id: string, displayName: string }[]
  selectedVendors: string[]
  onSelect: (id: string) => void
}) => (
  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
    <List
      itemLayout="horizontal"
      dataSource={vendors}
      renderItem={(item) => (
        <List.Item
          onClick={() => onSelect(item.id.toString())}
          style={{
            cursor: "pointer",
            background: selectedVendors.includes(item.id.toString()) ? blue[1] : "transparent",
            padding: 16,
          }}
        >
          {item.displayName}
        </List.Item>
      )}
    />
  </div>
);
